import React, { useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";

import { PageWrapper } from "../components/layout/container/Container";
import { PageHeader } from "../components/layout/header/Header";
import { PageHeading } from "../components/common/heading/Heading";
import BackButton from "../components/buttons/back/BackButton";

import "./Docs.css";

const UserTermsAndConditions = () => {
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "Katch | Terms & Conditions";
  }, []);

  return (
    <PageWrapper>
      <br />
      <br />
      <PageHeader>
        <BackButton onClick={() => navigate(-1)} />

        <PageHeading
          title="Terms & Conditions"
          subTitle="Terms governing your use of Katch and its services."
        />
      </PageHeader>

      <section className="docs__section">
        <p>Last updated: February 26, 2026</p>

        <p>
          Welcome to Katch.one ("Katch", "we", "us", or "our"). Katch is a
          sports technology platform that enables users, creators and sporting
          communities to stream, share, manage and monetise sports content.
        </p>

        <p>
          These Terms and Conditions ("Terms") govern your access to and use of
          Katch.one, including its website, applications, features, content and
          related services (collectively, the "Service").
        </p>

        <p>
          By accessing, registering for or using the Service, you agree to
          comply with these Terms. If you do not agree, you must not use the
          Service.
        </p>

        <h2>1. Acceptance of Terms</h2>

        <p>
          <strong>1.1.</strong> By accessing or using Katch, you acknowledge
          that you have read, understood and agreed to these Terms.
        </p>

        <p>
          <strong>1.2.</strong> These Terms apply to all users, including
          viewers, athletes, content creators, group administrators and other
          registered account holders.
        </p>

        <p>
          <strong>1.3.</strong> Certain features may be subject to additional
          terms, guidelines or requirements communicated through the Service.
        </p>

        <h2>2. User Registration and Accounts</h2>

        <p>
          <strong>2.1.</strong> Certain features require a registered user
          account. You agree to provide accurate, current and complete
          information when registering.
        </p>

        <p>
          <strong>2.2.</strong> You are responsible for maintaining the
          confidentiality of your account credentials and for activities carried
          out through your account.
        </p>

        <p>
          <strong>2.3.</strong> Users must be at least 13 years old to register.
          Users under 18 must have permission from a parent or legal guardian
          where required by applicable law.
        </p>

        <p>
          <strong>2.4.</strong> You must not impersonate another person, provide
          misleading account information or use another user's account without
          permission.
        </p>

        <p>
          <strong>2.5.</strong> You agree to notify Katch if you become aware of
          unauthorised access to your account.
        </p>

        <h2>3. User Access and Platform Features</h2>

        <p>
          <strong>3.1.</strong> Katch provides features that may include video
          streaming, live broadcasts, recorded replays, short-form videos known
          as Pops, photo albums, creator channels, groups and Talent ID
          profiles.
        </p>

        <p>
          <strong>3.2.</strong> Users may access free content and selected paid
          content, subject to the applicable access permissions, pricing and
          availability.
        </p>

        <p>
          <strong>3.3.</strong> Katch may introduce, update, restrict or
          discontinue features as the platform develops.
        </p>

        <p>
          <strong>3.4.</strong> Access to particular content may depend on its
          visibility settings, group membership, purchase status or other
          permissions established by the content creator or Katch.
        </p>

        <h2>4. User-Generated Content</h2>

        <p>
          <strong>4.1.</strong> Users and creators may upload, publish, manage
          or share content, including videos, Pops, photographs, live streams,
          profile information and other materials ("User Content").
        </p>

        <p>
          <strong>4.2.</strong> You are responsible for the User Content you
          upload, publish or distribute through Katch.
        </p>

        <p>
          <strong>4.3.</strong> You must own the necessary rights or have
          appropriate permission to upload, stream, distribute or monetise your
          User Content.
        </p>

        <p>
          <strong>4.4.</strong> Where required, you must obtain appropriate
          permissions from event organisers, sporting organisations, venue
          operators, copyright owners or other relevant parties before
          recording, publishing or commercially using content.
        </p>

        <p>
          <strong>4.5.</strong> You are responsible for obtaining any required
          consent when your content includes identifiable individuals,
          particularly children and young people.
        </p>

        <p>
          <strong>4.6.</strong> You must not upload content that infringes
          intellectual property rights, violates privacy, breaches applicable
          law or contains unlawful or harmful material.
        </p>

        <h2>5. Content Ownership and Licence</h2>

        <p>
          <strong>5.1.</strong> You retain ownership of the User Content you
          create and upload, subject to any rights held by third parties.
        </p>

        <p>
          <strong>5.2.</strong> By uploading User Content, you grant Katch a
          non-exclusive, worldwide licence to host, store, process, reproduce,
          display, distribute and stream that content as necessary to operate
          and provide the Service.
        </p>

        <p>
          <strong>5.3.</strong> This licence includes processing content for
          technical purposes such as video conversion, thumbnail generation,
          playback delivery and content storage.
        </p>

        <p>
          <strong>5.4.</strong> Katch does not acquire ownership of your User
          Content solely because it is uploaded to the platform.
        </p>

        <p>
          <strong>5.5.</strong> You may remove content you control, subject to
          applicable retention requirements, existing transactions and technical
          limitations.
        </p>

        <h2>6. Content Privacy and Groups</h2>

        <p>
          <strong>6.1.</strong> Katch may offer content visibility options
          including public, unlisted, private and group-restricted access.
        </p>

        <p>
          <strong>6.2.</strong> Public content may be discoverable by other
          users through creator channels, search results or other areas of the
          Service.
        </p>

        <p>
          <strong>6.3.</strong> Unlisted or restricted content may still be
          accessible to individuals who have been granted the relevant access
          permissions.
        </p>

        <p>
          <strong>6.4.</strong> Group creators and administrators are
          responsible for managing membership requests, access permissions and
          group-specific rules.
        </p>

        <p>
          <strong>6.5.</strong> Users must not share restricted content, access
          credentials or group access codes in ways that violate the permissions
          established by the relevant creator or administrator.
        </p>

        <h2>7. Live Streaming and Sporting Events</h2>

        <p>
          <strong>7.1.</strong> Katch may provide tools for broadcasting live
          sporting events, matches, training sessions and other activities.
        </p>

        <p>
          <strong>7.2.</strong> Creators are responsible for obtaining any
          required broadcasting, recording, venue and distribution permissions.
        </p>

        <p>
          <strong>7.3.</strong> Katch does not guarantee uninterrupted
          streaming, specific video quality or continuous availability of live
          broadcasts.
        </p>

        <p>
          <strong>7.4.</strong> Live broadcasts may be recorded, processed and
          made available as replays where recording is enabled and applicable
          permissions allow.
        </p>

        <p>
          <strong>7.5.</strong> Katch may restrict or remove streams that
          violate these Terms or applicable legal requirements.
        </p>

        <h2>8. Paid Content and Monetisation</h2>

        <p>
          <strong>8.1.</strong> Katch may enable users to purchase time-limited
          access to videos or other paid content.
        </p>

        <p>
          <strong>8.2.</strong> The applicable price, access period and any
          material purchase conditions will be presented before payment.
        </p>

        <p>
          <strong>8.3.</strong> Purchasing access to content does not transfer
          copyright or ownership of that content.
        </p>

        <p>
          <strong>8.4.</strong> Purchased content is intended for authorised
          personal viewing unless additional permissions are expressly provided.
        </p>

        <p>
          <strong>8.5.</strong> Paid access may expire at the end of the
          specified viewing period, and continued access may require another
          purchase.
        </p>

        <p>
          <strong>8.6.</strong> Creators who monetise content must have all
          necessary rights and permissions to do so.
        </p>

        <p>
          <strong>8.7.</strong> Content availability may be affected by
          technical issues, rights disputes, legal requirements or removal of
          content.
        </p>

        <h2>9. Payments and Refunds</h2>

        <p>
          <strong>9.1.</strong> Payments for content and other paid features are
          processed using payment methods supported by Katch and its payment
          service providers.
        </p>

        <p>
          <strong>9.2.</strong> By completing a purchase, you authorise the
          payment of the amount displayed at checkout.
        </p>

        <p>
          <strong>9.3.</strong> Refund requests will be assessed according to
          applicable consumer laws, including the Australian Consumer Law, and
          any additional refund policies communicated by Katch.
        </p>

        <p>
          <strong>9.4.</strong> Nothing in these Terms excludes, restricts or
          modifies consumer rights or remedies that cannot lawfully be excluded.
        </p>

        <p>
          <strong>9.5.</strong> Katch may suspend access to paid features where
          payment fails, is reversed or is reasonably suspected to be
          fraudulent, subject to applicable law.
        </p>

        <h2>10. Talent ID and User Profiles</h2>

        <p>
          <strong>10.1.</strong> Katch may provide Talent ID features that allow
          users to showcase sporting experience, achievements, abilities and
          related media.
        </p>

        <p>
          <strong>10.2.</strong> Users are responsible for the accuracy of
          information submitted to their profiles.
        </p>

        <p>
          <strong>10.3.</strong> Talent profiles, sporting achievements and
          assessments do not constitute a guarantee of selection, scholarships,
          employment or professional sporting opportunities.
        </p>

        <p>
          <strong>10.4.</strong> Users must obtain appropriate permissions
          before adding information, images or achievements belonging to another
          person.
        </p>

        <h2>11. Prohibited Activities</h2>

        <p>
          <strong>11.1.</strong> Users must not use Katch for unlawful,
          fraudulent, abusive or misleading activities.
        </p>

        <p>
          <strong>11.2.</strong> You must not copy, download, redistribute,
          resell or commercially exploit content without the necessary
          authorisation.
        </p>

        <p>
          <strong>11.3.</strong> You must not attempt to bypass payment
          requirements, content access controls, authentication systems or other
          security measures.
        </p>

        <p>
          <strong>11.4.</strong> You must not use automated systems to scrape,
          extract or collect platform content or personal information without
          authorisation.
        </p>

        <p>
          <strong>11.5.</strong> You must not interfere with the operation,
          security or availability of Katch or its supporting infrastructure.
        </p>

        <h2>12. Account Suspension and Termination</h2>

        <p>
          <strong>12.1.</strong> Katch may suspend, restrict or terminate
          accounts that violate these Terms, infringe third-party rights or
          create material security or legal risks.
        </p>

        <p>
          <strong>12.2.</strong> Katch may remove or restrict content when
          reasonably necessary to investigate or address a reported violation.
        </p>

        <p>
          <strong>12.3.</strong> Account termination may result in the loss of
          access to platform features and content, subject to applicable legal
          rights and obligations.
        </p>

        <h2>13. Service Availability</h2>

        <p>
          <strong>13.1.</strong> Katch aims to provide reliable access to its
          Service but does not guarantee that the platform will always be
          uninterrupted or error-free.
        </p>

        <p>
          <strong>13.2.</strong> Services may occasionally be unavailable
          because of maintenance, technical issues, infrastructure disruptions
          or circumstances beyond Katch's reasonable control.
        </p>

        <p>
          <strong>13.3.</strong> Features, supported formats and technical
          requirements may change as the platform evolves.
        </p>

        <h2>14. Liability and Consumer Rights</h2>

        <p>
          <strong>14.1.</strong> To the extent permitted by law, Katch is not
          responsible for losses arising from unauthorised user content,
          third-party services or circumstances outside its reasonable control.
        </p>

        <p>
          <strong>14.2.</strong> Nothing in these Terms excludes or limits any
          guarantee, right or remedy available under the Australian Consumer Law
          or other applicable legislation where such exclusion or limitation
          would be unlawful.
        </p>

        <h2>15. Changes to These Terms</h2>

        <p>
          <strong>15.1.</strong> Katch may update these Terms to reflect changes
          to the Service, business operations or legal requirements.
        </p>

        <p>
          <strong>15.2.</strong> Updated Terms will be published on Katch with a
          revised effective or last-updated date.
        </p>

        <p>
          <strong>15.3.</strong> Where required by applicable law, Katch will
          provide appropriate notice of material changes.
        </p>

        <h2>16. Governing Law</h2>

        <p>
          <strong>16.1.</strong> These Terms are governed by the laws of
          Victoria, Australia, and applicable Commonwealth laws.
        </p>

        <p>
          <strong>16.2.</strong> Disputes arising in connection with these Terms
          will be subject to the jurisdiction of the courts of Victoria,
          Australia, subject to applicable consumer rights and laws.
        </p>

        <h2>17. Contact and Support</h2>

        <p>
          If you have questions about these Terms, payment issues, content
          rights or the Service, please contact Katch through our{" "}
          <Link to="/support">Support Page</Link> or email{" "}
          <a href="mailto:support@katch.one">support@katch.one</a>.
        </p>

        <p>
          By continuing to access or use Katch, you acknowledge these Terms and
          agree to comply with them, subject to your rights under applicable
          law.
        </p>
      </section>
    </PageWrapper>
  );
};

export default UserTermsAndConditions;
